// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-SOC-1 — Site of Care → facility mapping: the wire contract.
// E-05: `api/client.ts` is frozen for new exports, so this domain lives here. E-19: the TS half
// of SiteOfCareBlockDto / FacilityRxItemDto in functions/MyPharmacy + functions/Admin — edited together.

import { apiFetch, parseApiError } from './client'

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  // apiFetch prepends API_BASE (/api) itself — pass the bare path.
  const res = await apiFetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw await parseApiError(res)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** One Rx's (or a patient's live) site of care. `facility_type` is a site_of_care_types.code or 'residential'. */
export interface SiteOfCareBlock {
  facility_type: string
  facility_type_label: string
  facility_id: string | null
  facility_name: string | null
  facility_phone: string | null
  facility_address: string | null
  match_method: 'address' | 'address_loose' | 'manual' | 'none'
  matched_address: string | null
  resolved_at: string | null
  // D-SOC-3: facility contact + operations (Pharmacy settings → Site of Care)
  contact_name?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  operating_hours?: string | null
  delivery_cutoff_time?: string | null
}

// ── D-SOC-3: per-account site-of-care scope (admin) ──────────────────────────────────────────
export type SiteOfCareScopeMode = 'all' | 'facility' | 'residential'
export const getUserSiteOfCareScope = (userId: string) =>
  req<{ scope: SiteOfCareScopeMode; facility_count: number }>('GET', `/users/${userId}/site-of-care-scope`)
export const setUserSiteOfCareScope = (userId: string, scope: SiteOfCareScopeMode) =>
  req<{ ok: boolean; scope: SiteOfCareScopeMode }>('PUT', `/users/${userId}/site-of-care-scope`, { scope })

export interface PatientSiteOfCare {
  available: boolean
  current: SiteOfCareBlock
  rx_snapshots: { facility_id: string | null; facility_type: string; facility_type_label: string; facility_name: string | null; rx: number; last_resolved_at: string }[]
}

export const getRxSiteOfCare = (rxId: string) =>
  req<{ available: boolean; site_of_care: SiteOfCareBlock | null }>('GET', `/rx/${rxId}/site-of-care`)

export const pinRxSiteOfCare = (rxId: string, facilityId: string | null) =>
  req<{ available: boolean; site_of_care: SiteOfCareBlock | null }>('PUT', `/rx/${rxId}/site-of-care`, { facility_id: facilityId, clear: false })

export const unpinRxSiteOfCare = (rxId: string) =>
  req<{ available: boolean; site_of_care: SiteOfCareBlock | null }>('PUT', `/rx/${rxId}/site-of-care`, { facility_id: null, clear: true })

export const getPatientSiteOfCare = (patientId: string) =>
  req<PatientSiteOfCare>('GET', `/patients/${patientId}/site-of-care`)

/**
 * One BATCH of the re-resolve sweep — not the whole tenant.
 *
 * The endpoint is deliberately bounded (ASP has ~54k Rx; an unbounded pass ran ~451s against a
 * 600s function timeout on a 0.5-1 vCore serverless DB). It reports `remaining` and `done` so a
 * caller can drive it to completion; this client type used to omit both, which is why the
 * button fired exactly one batch and stopped.
 */
export interface SiteOfCareResolveBatch {
  rx_resolved: number
  facilities: number
  by_type: Record<string, number>
  /** Rx still unresolved AFTER this batch. 0 means the sweep is complete. */
  remaining: number
  /** Server's own verdict — true when nothing is left to do. */
  done: boolean
  /** The batch size the server actually used (it clamps the request). */
  batch_size: number
  /** Cursor for a forced sweep; echo it back on the next batch. Null when not forcing. */
  since?: string | null
}

/**
 * @param force  re-evaluate rows the CURRENT resolver version already stamped. Needed after the
 *               facility DIRECTORY changes: the default predicate only re-resolves when the
 *               algorithm version moved, so importing facilities into an already-swept tenant
 *               otherwise returns {rx_resolved: 0, done: true} and silently does nothing.
 * @param since  cursor for a forced sweep — pass the value the previous batch echoed back, so
 *               rows re-resolved earlier in THIS run are not picked up again.
 */
export const resolveAllSiteOfCare = (batch?: number, force?: boolean, since?: string) => {
  const q = new URLSearchParams()
  if (batch) q.set('batch', String(batch))
  if (force) q.set('force', '1')
  if (since) q.set('since', since)
  const qs = q.toString()
  return req<SiteOfCareResolveBatch>('POST', `/site-of-care/resolve${qs ? `?${qs}` : ''}`)
}

export interface SiteOfCareSummary {
  available: boolean
  total_rx: number
  residential_rx: number
  items: { facility_id: string; facility_type: string; rx: number; patients: number; last_resolved_at: string }[]
}
export const getSiteOfCareSummary = () => req<SiteOfCareSummary>('GET', '/site-of-care/summary')

// ── Facility Staff grant (admin) ──────────────────────────────────────────────────────────────
export interface FacilityGrant { facility_id: string; name: string; facility_type: string | null; city: string | null; is_active: boolean }
export const getUserFacilityAccess = (userId: string) =>
  req<{ grants: FacilityGrant[]; count: number }>('GET', `/users/${userId}/facility-access`)
export const setUserFacilityAccess = (userId: string, facilityIds: string[]) =>
  req<{ ok: boolean; count: number }>('PUT', `/users/${userId}/facility-access`, { facility_ids: facilityIds })

// ── Facility portal (the external facility's own surface) ─────────────────────────────────────
export interface FacilityMe {
  facilities: { id: string; name: string; facility_type: string | null; facility_type_label: string | null; address: string; phone: string | null }[]
  count: number
}
export interface FacilityRxItem {
  /** Prescriber display name. Server-redacted via the 'prescribers' table, so a pharmacy that
   *  masks prescriber names sees the mask here too. */
  prescriber?: string | null
  rx_id: string
  rx_number: string | null
  patient: string | null
  drug: string | null
  strength: string | null
  quantity: number | null
  days_supply: number | null
  state: string | null
  ship_status: string | null
  ship_tracking: string | null
  ship_delivered_at: string | null
  last_activity_at: string | null
  facility_id: string | null
  facility_name: string | null
  /** Values of the pharmacy's configured custom columns, keyed "list_columns.<column>". */
  custom?: Record<string, string | null> | null
}
export interface FacilityRxPage {
  items: FacilityRxItem[]; count: number; total: number; page: number; page_size: number; total_pages: number; truncated: boolean
  /** D-SOC-8 — the pharmacy's configured visible columns for this list (Facility view builder).
   *  Absent/null means "never configured": render the defaults. This is the CONFIGURATION, not a
   *  property of the rows — a page where an optional column happens to be all-null must still
   *  show that column, or the header would change from page to page. */
  columns?: string[] | null
}
export const getFacilityMe = () => req<FacilityMe>('GET', '/facility/me')
export const listFacilityRx = (p: {
  q?: string; facility?: string; page?: number; page_size?: number
  /** D-SOC-10 B-2: server-side sort. Ignored by the server unless the key is allow-listed. */
  order_by?: string; order_dir?: 'asc' | 'desc'
  /** `key:op:v1,v2` — the same contract the Rx list speaks, so the shared table needs no
   *  facility special case. An unknown key is ignored server-side, never 400. */
  filter?: string[]
}) => {
  const qs = new URLSearchParams()
  if (p.q) qs.set('q', p.q)
  if (p.facility) qs.set('facility', p.facility)
  if (p.order_by) qs.set('order_by', p.order_by)
  if (p.order_dir) qs.set('order_dir', p.order_dir)
  for (const f of p.filter ?? []) if (f) qs.append('filter', f)
  qs.set('page', String(p.page ?? 1))
  qs.set('page_size', String(p.page_size ?? 50))
  return req<FacilityRxPage>('GET', `/facility/rx?${qs.toString()}`)
}

/** Distinct values for one facility-list column, for the header's filter picker.
 *  Without this the caps map says "filterable" and the picker has nothing to offer — which is
 *  exactly what Mario saw as "every single col is not filterable". */
export const getFacilityRxFacet = (p: { column: string; q?: string; facility?: string }) => {
  const qs = new URLSearchParams()
  qs.set('column', p.column)
  if (p.q) qs.set('q', p.q)
  if (p.facility) qs.set('facility', p.facility)
  // Returns the SAME envelope as the provider facet (RxFacetResponse), because the shared
  // ColumnFilterButton reads `options` — not `values`. This originally returned { values }, so
  // every facility funnel opened to an empty list while the caps map and the SQL were both fine.
  return req<{
    column: string
    klass: string
    source: 'configured' | 'distinct' | 'entity' | 'range' | 'not_enumerable'
    options: { value: string; label: string; count?: number }[]
    truncated: boolean
    notice: string | null
  }>('GET', `/facility/rx/facet?${qs.toString()}`)
}

/** One resident Rx, field-by-field under the pharmacy's Facility Staff field rules. Hidden fields are ABSENT. */
export interface FacilityRxDetail {
  rx_id: string
  rx: Record<string, unknown>
  patient: Record<string, unknown>
  prescriber: Record<string, unknown>
  facility: { id: string | null; name: string | null; type_label: string | null; phone: string | null } | null
  shipment: { id: string; status: string | null; carrier: string | null; tracking_number: string | null; service_level: string | null; shipped_at: string | null; delivered_at: string | null; last_tracked_at: string | null; carrier_status_text: string | null } | null
  events: { status: string | null; carrier_status_text: string | null; description: string | null; location: string | null; occurred_at: string | null }[]
}
export const getFacilityRx = (rxId: string) => req<FacilityRxDetail>('GET', `/facility/rx/${rxId}`)

/** D-SOC-2: a prescriber's site of care is an aggregate over their patients' current site of care. */
export interface PrescriberSiteOfCare {
  available: boolean
  facilities_served: string | null
  facility_patient_count: number
  residential_patient_count: number
  facilities: { facility_id: string; facility_type: string; facility_type_label: string; facility_name: string | null; patients: number; rx: number }[]
}
export const getPrescriberSiteOfCare = (prescriberId: string) => req<PrescriberSiteOfCare>('GET', `/prescribers/${prescriberId}/site-of-care`)

/** Display label for a site-of-care type code on list rows (the settings tab owns the full registry). */
export const SITE_OF_CARE_LABELS: Record<string, string> = {
  residential: 'Residential', snf: 'SNF / Nursing home', board_care: 'Board & Care', infusion_center: 'AIC',
  ais: 'AIS', clinic_site: 'Clinic / Physician office', home: 'Home', self_admin: 'Self-administered',
}
export const siteOfCareLabel = (code: string | null | undefined): string => code ? (SITE_OF_CARE_LABELS[code] ?? code) : ''

